/* eslint-disable */
import type { LocalizedString } from "../../cash/localization/localization.pb";
import type { AlertUi, AllocationDestination, LocalizedTemplatedString } from "../../cash/paychecks/common.pb";
import type { Color } from "../../cash/ui/Color.pb";
import type { Image } from "../../cash/ui/Image.pb";
import type { Money } from "../../common/currency.pb";

/**
 * A blocker that mobile clients will consume to render the paycheck
 * allocation amount selector.
 */
export interface SetPaycheckAllocationAmountBlocker {
  /** Array of all destinations and associated UI configuration */
  initial_destination_states?: SetPaycheckAllocationAmountBlockerDestinationUiConfiguration[];
  /**
   * An index into the destination array, specifying which destination
   * we are currently editing the allocation for
   */
  initially_selected_destination_index?: number;
  /**
   * A UI specification for an alert shown when a user tries to allocate
   * more than 100% (or 10,000 bps) in total
   */
  exceeded_max_distribution_alert_ui?: AlertUi;
  /** Used to calculate the estimated values placed in `estimated_currency_amount_template` */
  average_monthly_paycheck?: Money;
}

/** The UI Configuration of a single destination in the chart */
export interface SetPaycheckAllocationAmountBlockerDestinationUiConfiguration {
  /** The destination being configured. */
  destination?: AllocationDestination;
  /**
   * The initially set share in basis points to display. Includes
   * unsaved edits from earlier in the flow. The sum of these values
   * across all destinations should be 10,000 including the
   * (uneditable) Cash Balance allocation. Both server and client
   * should validate this.
   *
   * (100 bps == 1.0%)
   * (1 bps == 0.01%)
   */
  share_in_basis_points?: number;
  /**
   * The color associated with this destination, used for the pie
   * chart segment and drag handle, the ATM picker tint, the custom
   * % selector, and the primary button.
   */
  tint_color?: Color;
  /**
   * What happens when this destination is not selected and the
   * customer taps on its segment in the pie chart. For the
   * initially selected destination, this value is not applicable
   * unless there is some other destination present that specifies
   * `ALLOW_IMMEDIATE_SELECTION`.
   *
   * If ALLOW_IMMEDIATE_SELECTION or if this destination is found at
   * `initially_selected_destination_index`, the
   * `selected_state_ui_elements` field must be set.
   *
   * The destination at `initially_selected_destination_index` does
   * not strictly depend on this value because it only applies to
   * the behavior when _becoming_ selected, but for the Paychecks MVP
   * we should set it to `ALLOW_IMMEDIATE_SELECTION`.
   */
  selection_behavior?: SetPaycheckAllocationAmountBlockerDestinationUiConfigurationDestinationSelectionBehavior;
  /**
   * A UI specification of the elements to display when this
   * destination is selected (being edited)
   */
  selected_state_ui_elements?: SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElements;
}

/**
 * What happens when the customer attempts to select (by tapping) on
 * this destination's segment in the chart. Only applicable when the
 * destination was not already selected.
 */
export enum SetPaycheckAllocationAmountBlockerDestinationUiConfigurationDestinationSelectionBehavior {
  SELECTION_BEHAVIOR_DO_NOT_USE = "SELECTION_BEHAVIOR_DO_NOT_USE",
  /**
   * NOT_SELECTABLE - Use for allocation destinations that can't be edited, such as
   * Cash Balance or to disallow jumping to a different destination.
   * For the Paychecks MVP, we should always use this option.
   */
  NOT_SELECTABLE = "NOT_SELECTABLE",
  /**
   * SUBMIT_ON_SELECTION - (Not implemented in Paychecks MVP) Submit the blocker form when tapped
   * and proceed
   */
  SUBMIT_ON_SELECTION = "SUBMIT_ON_SELECTION",
  /**
   * ALLOW_IMMEDIATE_SELECTION - (Not implemented in Paychecks MVP) Allow the customer to begin editing
   * this destination without submitting and proceeding.
   */
  ALLOW_IMMEDIATE_SELECTION = "ALLOW_IMMEDIATE_SELECTION",
}

/**
 * A UI Specification of the vertical stack of UI elements to
 * display when this destination is selected
 */
export interface SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElements {
  /**
   * The text specifying the name of the destination at the top of
   * the screen.
   */
  header?: LocalizedString;
  /**
   * Template string for clients to display the estimated amount
   * per paycheck.
   *
   * Format specifier is %s for Android and %@ for iOS. Example:
   *   - Android: "%s based on average monthly paycheck"
   *   - iOS:     "%@ based on average monthly paycheck"
   */
  estimated_currency_amount_template?: LocalizedTemplatedString;
  /**
   * Options for the "ATM picker", expressed as basis points.
   * (100 bps == 1.0%)
   * (1 bps == 0.01%)
   * e.g. 1%, 5%, 10%, 15%, 20% -> 100, 500, 1000, 1500, 2000
   *
   * There should be exactly 5 elements in this array. The 6th
   * element is "..." and will show a custom percentage chooser that
   * uses the `custom_amount_subtitle``.
   */
  atm_picker_options_in_basis_points?: number[];
  /**
   * The text to display under the header when choosing a custom %
   * to allocate to this destination.
   */
  custom_amount_subtitle?: LocalizedString;
  /**
   * If nil, don't show a configuration row. For the Paychecks MVP,
   * this is only used for Stocks.
   */
  configuration_row?:
    SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElementsConfigurationRow;
  /** The primary button. This is likely the "Confirm" button */
  primary_button?: SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElementsButton;
  /** The secondary button. This is likely nil or "Turn Off" */
  secondary_button?: SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElementsButton;
}

/**
 * UI specification for the configuration row, used in the
 * Paychecks MVP for selecting a stock.
 *
 * Fields are laid out in a horizontal stack from the leading
 * edge. Tapping the configuration rows always submits the
 * blocker.
 */
export interface SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElementsConfigurationRow {
  /** For Paychecks MVP, the investment's image. */
  image?: Image;
  /** For Paychecks MVP, the investment's display name "Apple". */
  title_text?: LocalizedString;
  /**
   * For Paychecks MVP, the investment's parenthesized ticker
   * (AAPL).
   */
  subtitle_text?: LocalizedString;
  /** For Paychecks MVP, the call to action to change the investment choice. */
  action_text?: LocalizedString;
}

/**
 * UI specification of the buttons appearing at the bottom of
 * the blocker
 */
export interface SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElementsButton {
  /** The action to submit the blocker with */
  action?: SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElementsButtonButtonAction;
  /** The button text */
  label?: LocalizedString;
}

/**
 * The action to specify when submitting the blocker after
 * tapping this button
 */
export enum SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElementsButtonButtonAction {
  BUTTON_ACTION_DO_NOT_USE = "BUTTON_ACTION_DO_NOT_USE",
  /** CONFIRM - Proceed to confirmation */
  CONFIRM = "CONFIRM",
  /** DISABLE - Proceed to confirmation for disabling this allocation */
  DISABLE = "DISABLE",
}
